import React, { useEffect, useMemo, useRef, useState } from "react";
import useMediaQuery from "../hooks/useMediaQuery";

const norm = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const MAX_RESULTS = 8;

// "#12", "id 12", "id:12", "boat 12" -> explicit ID lookup. A bare "12" matches ship ID 12 first, then falls back to normal text search.
const ID_PREFIXED = /^\s*(?:#|(?:boat|ship|wreck)?\s*id\s*[:#]?|boat\s*[:#]?|ship\s*[:#]?)\s*(\d{1,9})\s*$/i;
const ID_BARE = /^\s*(\d{1,9})\s*$/;

function highlight(text, tokens) {
  const t = String(text ?? "");
  const n = norm(t);
  // find the first token occurrence (normalised string has same length for most text)
  for (const tok of tokens) {
    const i = n.indexOf(tok);
    if (i >= 0 && n.length === t.length) {
      return (<>{t.slice(0, i)}<mark>{t.slice(i, i + tok.length)}</mark>{t.slice(i + tok.length)}</>);
    }
  }
  return t;
}

export default function SearchBar({ ships, onSelect }) {
  const coarse = useMediaQuery("(pointer: coarse)"); // touch screens have no "/" shortcut
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const tokens = useMemo(() => norm(term).split(/\s+/).filter(Boolean), [term]);

  const results = useMemo(() => {
    if (!tokens.length) return [];
    const prefixed = term.match(ID_PREFIXED);
    const bare = term.match(ID_BARE);
    const idNum = prefixed ? Number(prefixed[1]) : bare ? Number(bare[1]) : null;
    if (prefixed) return ships.filter((s) => Number(s.id) === idNum); // explicit ID search: exact matches only
    const scored = [];
    for (const ship of ships) {
      const idHit = idNum !== null && Number(ship.id) === idNum;
      const title = norm(ship.title);
      const rest = norm([ship.type, ship.owner, ship.builder, ship.port_of_registry, ship.date, ship.imo && String(ship.imo), ship.submitted_by].filter((v) => v && v !== "unknown").join(" "));
      const hay = `${title} ${rest}`;
      if (!idHit && !tokens.every((t) => hay.includes(t))) continue; // every word must match somewhere
      let score = idHit ? 1000 : 0;
      if (title === tokens.join(" ")) score += 100;
      if (title.startsWith(tokens[0])) score += 40;
      if (tokens.every((t) => title.includes(t))) score += 20;
      if (title.split(/\s+/).some((w) => w.startsWith(tokens[0]))) score += 10;
      scored.push({ ship, score });
    }
    scored.sort((a, b) => b.score - a.score || String(a.ship.title).localeCompare(String(b.ship.title)));
    return scored.map((x) => x.ship);
  }, [ships, tokens, term]);

  const shown = results.slice(0, MAX_RESULTS);

  useEffect(() => setActive(0), [term]);

  // close when clicking elsewhere
  useEffect(() => {
    const onDown = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // "/" focuses the search (unless you're typing somewhere)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "/" && !["input", "textarea", "select"].includes(tag) && !e.target.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const choose = (ship) => {
    onSelect(ship);
    setTerm("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && shown[active]) { e.preventDefault(); choose(shown[active]); }
    else if (e.key === "Escape") { if (term) setTerm(""); else { setOpen(false); inputRef.current?.blur(); } }
  };

  const showList = open && tokens.length > 0;

  return (
    <div className="search-container" ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={showList}
        aria-controls="ship-search-results"
        aria-autocomplete="list"
        autoComplete="off"
        spellCheck={false}
        placeholder={coarse ? "Search ships or ID…" : "Search ships or ID (#12)…  ( / )"}
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="search-bar"
      />
      {term && (
        <button className="search-clear" onClick={() => { setTerm(""); inputRef.current?.focus(); }} aria-label="Clear search">×</button>
      )}

      {showList && (
        <div className="search-dropdown" id="ship-search-results" role="listbox">
          {shown.length === 0 ? (
            <div className="search-empty">No ships match “{term}”{ID_PREFIXED.test(term) ? " — no boat with that ID" : ""}</div>
          ) : (
            <>
              {shown.map((ship, i) => (
                <div
                  key={ship.id}
                  role="option"
                  aria-selected={i === active}
                  className={`search-result${i === active ? " active" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(ship)}
                >
                  {ship.images?.[0] ? (
                    <img src={ship.images[0]} alt="" loading="lazy" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                  ) : (
                    <div className="search-thumb-fallback">⚓</div>
                  )}
                  <div className="search-result-text">
                    <span className="search-result-title">{highlight(ship.title, tokens)}</span>
                    <small>{[`ID ${ship.id}`, ship.type, ship.date].filter((v) => v && v !== "unknown").join(" · ")}</small>
                  </div>
                </div>
              ))}
              {results.length > MAX_RESULTS && (
                <div className="search-more">+{results.length - MAX_RESULTS} more — keep typing to narrow down</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
